// QR de la modalidad "en el teléfono del cliente", compartido por KYC, selfie, comprobante y la
// guía del equipo. El código va con al menos 4 px por módulo.
import { useState } from 'react';
import { toast } from 'sonner';
import { Check, Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/kit';
import { QR_MARGIN_MODULES, QR_MIN_DISPLAY_PX, qrMinWidthPx } from '../qrSizing';

export interface CustomerLinkQrProps {
    /** URL que se codifica. Puede ser la del widget hospedado o su versión acortada. */
    url: string;
    /** Qué tiene que hacer el vendedor con el código. */
    intro: string;
    /** Qué pasa después de que el cliente escanea. */
    hint: string;
    /** Nombre accesible del código: un `<svg>` sin título no lo anuncia ningún lector de pantalla. */
    title: string;
}

/** Solo el código. `level="L"` porque la URL es larga; fondo blanco fijo para el tema oscuro. */
export function LinkQrCode({ url, title }: { url: string; title: string }) {
    const widthPx = Math.max(qrMinWidthPx(url), QR_MIN_DISPLAY_PX);
    return (
        <div className="w-fit flex-none rounded-lg border border-border bg-white p-3">
            <QRCodeSVG
                value={url}
                title={title}
                size={512}
                level="L"
                marginSize={QR_MARGIN_MODULES}
                style={{ width: `${String(widthPx)}px`, height: 'auto' }}
            />
        </div>
    );
}

/** «Copiar liga» con su confirmación de 2 s. */
export function CopyLinkButton({ url, disabled }: { url: string; disabled?: boolean }) {
    const [copied, setCopied] = useState(false);

    const copyLink = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
            }, 2000);
        } catch {
            toast.error('No se pudo copiar la liga.');
        }
    };

    return (
        <Button
            variant="secondary"
            size="sm"
            disabled={disabled}
            leftIcon={
                copied ? (
                    <Check className="size-3.5" aria-hidden />
                ) : (
                    <Copy className="size-3.5" aria-hidden />
                )
            }
            onClick={() => void copyLink()}
        >
            {copied ? 'Liga copiada' : 'Copiar liga'}
        </Button>
    );
}

/** Código con el texto a un lado en pantallas anchas. */
export function CustomerLinkQr({ url, intro, hint, title }: CustomerLinkQrProps) {
    return (
        <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:items-center sm:text-left">
            <LinkQrCode url={url} title={title} />

            <div className="flex flex-col items-center gap-3 sm:items-start">
                <p className="text-[12.5px] text-muted-foreground">{intro}</p>
                <p className="max-w-xs text-[11.5px] leading-relaxed text-muted-foreground">
                    {hint}
                </p>
                <CopyLinkButton url={url} />
            </div>
        </div>
    );
}
